import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronRight, Loader2, Layers, AlertTriangle } from 'lucide-react';

const fmtFecha = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return '—'; }
};

/**
 * Vista AGRUPADA de casos por CVU (reincidencias del mismo cliente).
 * Filas desplegables con chevron: la principal (más reciente) + los "otros".
 */
export default function CasesGroupedView({ searchTerm, filters, estados = [], onOpenCase }) {
  const [data, setData] = useState({ grupos: [], total_grupos: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({}); // key -> bool

  const estadoLabel = useCallback((val) => {
    const e = estados.find((x) => x.value === val || x.key === val);
    return e?.label || val || '—';
  }, [estados]);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 25 };
      if (filters?.estado) params.estado = filters.estado;
      if (filters?.prioridad) params.prioridad = filters.prioridad;
      if (searchTerm) params.search = searchTerm;
      const r = await api.get('/cases-grouped', { params });
      setData(r.data);
    } catch {
      setData({ grupos: [], total_grupos: 0, page: 1, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [filters?.estado, filters?.prioridad, searchTerm]);

  useEffect(() => { setPage(1); }, [filters?.estado, filters?.prioridad, searchTerm]);
  useEffect(() => { load(page); }, [load, page]);

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const PrioBadge = ({ p }) => {
    if (!p) return null;
    const cls = p === 'CRITICA' ? 'bg-red-100 text-red-700' : p === 'ALTA' ? 'bg-orange-100 text-orange-700'
      : p === 'MEDIA' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600';
    return <Badge className={`${cls} text-[10px]`}>{p}</Badge>;
  };

  return (
    <Card className="bg-white overflow-hidden" data-testid="cases-grouped-view">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2 text-sm text-slate-600">
        <Layers size={16} className="text-indigo-500" />
        Vista agrupada por CVU — <span className="font-semibold text-slate-700">{data.total_grupos}</span> grupo(s)
        <span className="text-xs text-slate-400 ml-2">Los grupos con más de 1 caso son reincidencias del mismo cliente.</span>
      </div>

      {loading ? (
        <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin text-indigo-500" size={26} /></div>
      ) : data.grupos.length === 0 ? (
        <div className="p-10 text-center text-slate-400 text-sm">No se encontraron casos.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {data.grupos.map((g) => {
            const key = `${g.key_tipo}:${g.key_valor}`;
            const isOpen = !!expanded[key];
            const hasOtros = (g.otros || []).length > 0;
            const p = g.principal || {};
            return (
              <div key={key} data-testid={`group-${g.key_valor}`}>
                {/* Fila principal */}
                <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                  <button
                    onClick={() => hasOtros && toggle(key)}
                    className={`p-1 rounded ${hasOtros ? 'hover:bg-slate-200 text-slate-500' : 'text-transparent cursor-default'}`}
                    data-testid={`group-toggle-${g.key_valor}`}
                    aria-label="expandir"
                  >
                    <ChevronRight size={16} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </button>

                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpenCase?.(p.id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800 text-sm truncate max-w-[280px]">{p.titulo || '—'}</span>
                      {g.cantidad > 1 && (
                        <Badge className="bg-indigo-100 text-indigo-700 text-[10px]" data-testid={`group-count-${g.key_valor}`}>
                          {g.cantidad} casos
                        </Badge>
                      )}
                      <PrioBadge p={p.prioridad} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[11px] text-slate-400">{g.key_tipo.toUpperCase()} {g.key_valor}</span>
                      {(g.estados || []).map((e) => (
                        <Badge key={e} variant="outline" className="text-[10px] text-slate-500 border-slate-200">{estadoLabel(e)}</Badge>
                      ))}
                    </div>
                  </div>

                  <div className="hidden sm:flex items-center gap-1 text-xs text-amber-600 shrink-0" title="Total alertas del grupo">
                    <AlertTriangle size={13} /> {g.total_alertas_grupo}
                  </div>
                  <div className="text-xs text-slate-400 w-16 text-right shrink-0">{fmtFecha(g.ultima_actividad)}</div>
                  <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                    onClick={() => onOpenCase?.(p.id)} data-testid={`group-open-${g.key_valor}`}>
                    Abrir
                  </Button>
                </div>

                {/* Sub-filas (otros casos del grupo) */}
                {isOpen && hasOtros && (
                  <div className="bg-slate-50/60 border-t border-slate-100">
                    {g.otros.map((o) => (
                      <div key={o.id} className="flex items-center gap-3 pl-12 pr-4 py-2 hover:bg-slate-100 cursor-pointer"
                        onClick={() => onOpenCase?.(o.id)} data-testid={`group-child-${o.id}`}>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-slate-700 truncate">{o.titulo || '—'}</span>
                          <Badge variant="outline" className="ml-2 text-[10px] text-slate-500 border-slate-200">{estadoLabel(o.estado)}</Badge>
                        </div>
                        <div className="hidden sm:flex items-center gap-1 text-xs text-amber-600 shrink-0">
                          <AlertTriangle size={12} /> {o.total_alertas}
                        </div>
                        <div className="text-xs text-slate-400 w-16 text-right shrink-0">{fmtFecha(o.updated_at || o.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {data.total_pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <span className="text-sm text-slate-500">Página {data.page} de {data.total_pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((x) => x - 1)} data-testid="grouped-prev">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page >= data.total_pages || loading} onClick={() => setPage((x) => x + 1)} data-testid="grouped-next">Siguiente</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
